import wsPrintClient from './wsPrintClient';
import notaanota from './notaanota';
import { message } from 'antd';

const WEB_BLUETOOTH_TYPE = 'webbluetooth';
const WEB_BLUETOOTH_CHUNK_SIZE = 512;
const ZEBRA_BLE_SERVICE_UUID = '38eb4a80-c570-11e3-9507-0002a5d5c51b';
const ZEBRA_BLE_WRITE_CHARACTERISTIC_UUID = '38eb4a82-c570-11e3-9507-0002a5d5c51b';
const VALID_PRINTER_TYPES = ['bluetooth', 'websocket', 'socket', WEB_BLUETOOTH_TYPE];
const INVALID_PRINTER_NAMES = new Set(['bluetooth', 'websocket', 'socket']);

const sanitizePrinterName = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const normalized = raw
    .replace(/^bluetooth[\s:|-]*/i, '')
    .replace(/^websocket[\s:|-]*/i, '')
    .replace(/^socket[\s:|-]*/i, '')
    .trim();

  if (!normalized) return null;
  if (INVALID_PRINTER_NAMES.has(normalized.toLowerCase())) return null;

  return normalized;
};

const extractPrinterName = (input) => {
  if (input == null) return null;

  if (typeof input === 'string') {
    const parsed = input.trim();
    if (!parsed) return null;

    if ((parsed.startsWith('{') && parsed.endsWith('}')) || (parsed.startsWith('[') && parsed.endsWith(']'))) {
      try {
        const nested = extractPrinterName(JSON.parse(parsed));
        if (nested) return nested;
      } catch (error) {
        void error;
      }
    }

    return sanitizePrinterName(parsed);
  }

  if (typeof input === 'object') {
    const candidates = [
      input.deviceName,
      input.printerName,
      input.nome,
      input.name,
      input.label,
      input.device,
      input.impressora,
      input.selectedPrinter,
      input.printer,
      input?.device?.name,
      input?.device?.nome,
      input?.device?.label,
      input?.payload?.deviceName,
      input?.payload?.printerName,
      input?.payload?.name,
      input?.payload?.nome,
    ];

    for (const candidate of candidates) {
      const normalized = extractPrinterName(candidate);
      if (normalized) return normalized;
    }
  }

  return null;
};
/**
 * Serviço para gerenciar conexões de impressoras
 * Suporta Bluetooth (Android) e WebSocket (Etiquetando Print)
 */

class PrinterService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.connectionType = null;
    this.printerType = null;
    this.printerName = null;
    this.deviceInfo = null;
    this.activeLabelCode = null;
    this.currentUser = null;
    this.webBluetoothDevice = null;
    this.webBluetoothServer = null;
    this.webBluetoothCharacteristic = null;
    this.handleWebBluetoothDisconnected = this.handleWebBluetoothDisconnected.bind(this);
    this.callbacks = {
      onConnect: null,
      onDisconnect: null,
      onError: null,
      onDeviceList: null
    };

    try {
      const savedName = localStorage.getItem('printerName');
      const savedType = localStorage.getItem('printerType');
      if (savedName) {
        this.printerName = savedName;
        this.isConnected = true;
      }
      if (savedType) {
        this.connectionType = savedType;
        this.printerType = savedType;
      }
    } catch { }
  }

  syncState(state = {}) {
    const connected = typeof state.connected === 'boolean' ? state.connected : undefined;
    const name = extractPrinterName(state.name);
    const type = typeof state.type === 'string' ? state.type : undefined;
    const device = state.device && typeof state.device === 'object' ? state.device : undefined;
    if (connected !== undefined) this.isConnected = connected;
    if (name) this.printerName = name;
    if (type && VALID_PRINTER_TYPES.includes(type)) {
      this.connectionType = type === 'socket' ? 'socket' : type;
      this.printerType = type;
    }
    if (device) {
      const normalizedDeviceName = extractPrinterName(device);
      this.deviceInfo = { ...(this.deviceInfo || {}), ...device };
      if (normalizedDeviceName) this.deviceInfo.nome = normalizedDeviceName;
    }
  }

  /**
   * Registra callbacks para eventos da impressora
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  setActiveLabelCode(code) {
    const v = typeof code === 'string' ? code : null;
    this.activeLabelCode = v && v.trim() !== '' ? v : null;
  }

  async ensureActiveLabelCode() {
    if (!this.activeLabelCode || this.activeLabelCode === '') {
      const savedLayout = localStorage.getItem('layoutEtiqueta');
      if (savedLayout) {
        this.activeLabelCode = savedLayout;
      }
    }
    return this.activeLabelCode || null;
  }

  setUser(user) {
    if (user && typeof user === 'object') {
      this.currentUser = user;
    } else {
      this.currentUser = null;
    }
  }

  setprintertype(type) {
    if (VALID_PRINTER_TYPES.includes(type)) {
      this.printerType = type;
      this.connectionType = type === 'socket' ? 'socket' : this.connectionType;
      localStorage.setItem('printerType', type);
    } else {
      console.error('Tipo de impressora inválido. Use "bluetooth", "webbluetooth" ou "websocket".');
    }
  }

  setPrinterType(type) {
    return this.setprintertype(type);
  }

  /**
   * Lista dispositivos Bluetooth disponíveis
   * Funciona apenas em dispositivos Android com interface nativa
   */
  async listBluetoothDevices() {
    try {
      if (!window.Android) {
        throw new Error('Funcionalidade disponível apenas em dispositivos Android');
      }

      // Chama a função nativa do Android
      const result = await window.Android.listaBluetooth();

      let devices = [];
      try {
        if (typeof result === 'string') {
          devices = JSON.parse(result);
        } else {
          devices = result;
        }
      } catch (e) {
        console.error('Erro ao fazer parse da lista de dispositivos Bluetooth:', e);
        devices = [];
      }

      if (this.callbacks.onDeviceList) {
        this.callbacks.onDeviceList(devices);
      }

      return devices;
    } catch (error) {
      console.error('Erro ao listar dispositivos Bluetooth:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message);
      }
      throw error;
    }
  }

  /**
   * Conecta a um dispositivo Bluetooth
   */
  async connectBluetooth(deviceAddress) {
    try {
      if (!window.Android) {
        throw new Error('Funcionalidade disponível apenas em dispositivos Android');
      }

      const addr = (typeof deviceAddress === 'string')
        ? deviceAddress
        : (deviceAddress?.address || deviceAddress?.mac || deviceAddress?.id || deviceAddress?.uuid || deviceAddress?.device || deviceAddress?.nome || deviceAddress?.name || '');
      const nome = (typeof deviceAddress === 'string')
        ? null
        : extractPrinterName(deviceAddress);

      if (!addr || String(addr).trim() === '') {
        throw new Error('Endereço/ID do dispositivo Bluetooth inválido');
      }

      await window.Android.conectarBluetooth(addr);

      this.connectionType = 'bluetooth';
      this.printerType = 'bluetooth';
      this.isConnected = true;
      this.printerName = nome || extractPrinterName(deviceAddress) || sanitizePrinterName(addr) || addr;
      this.deviceInfo = { ...(this.deviceInfo || {}), address: addr, nome: this.printerName || undefined };

      // Persistir no localStorage para que ao recarregar o app saiba qual impressora estava conectada
      try {
        localStorage.setItem('printerType', 'bluetooth');
        localStorage.setItem('printerName', this.printerName);
      } catch (e) { void e; }

      if (this.callbacks.onConnect) {
        this.callbacks.onConnect({ type: 'bluetooth', device: this.printerName, status: 'connected' });
      }

      return true;
    } catch (error) {
      console.error('Erro ao conectar Bluetooth:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message);
      }
      throw error;
    }
  }

  /**
   * Conecta via WebSocket (Etiquetando Print)
   */
  async connectWebSocket(uuid) {
    return new Promise((resolve, reject) => {
      try {
        const user = this.currentUser || null;
        let clientUUID = null;
        try {
          clientUUID = localStorage.getItem('client_uuid');
          if (!clientUUID) {
            clientUUID = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
            localStorage.setItem('client_uuid', clientUUID);
          }
        } catch (error) {
          void error;
          clientUUID = String(Date.now());
        }

        const ident = {
          id: clientUUID,
          branches: { sigla: user?.assignedBranch?.sigla || user?.assignedBranch?.acronym || '' },
          server: false,
          computer: window.navigator.userAgent,
          codigo: user?.codigo || 'on board',
          name: user?.name || 'on board',
          emparelhar: true,
          versao: 'on board',
          pairWithUuid: String(uuid),
        };

        wsPrintClient.initializeWebsocket(ident)
          .then(() => {
            this.isConnected = true;
            this.connectionType = 'websocket';
            this.printerType = 'websocket';
            this.deviceInfo = { uuid };
            resolve(true);
          })
          .catch((error) => {
            if (this.callbacks.onError) {
              this.callbacks.onError('Erro na conexão WebSocket');
            }
            reject(error);
          });
      } catch (error) {
        if (this.callbacks.onError) {
          this.callbacks.onError(error.message);
        }
        reject(error);
      }
    });
  }

  isWebBluetoothSupported() {
    return typeof navigator !== 'undefined' && !!navigator.bluetooth;
  }

  isWebBluetoothSecureContext() {
    if (typeof window === 'undefined') return false;
    const hostname = window.location?.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalhost) return true;
    if (window.isSecureContext) return true;
    return window.location?.protocol === 'https:';
  }

  clearWebBluetoothState({ notifyDisconnect = false } = {}) {
    if (this.webBluetoothDevice && this.handleWebBluetoothDisconnected) {
      try {
        this.webBluetoothDevice.removeEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);
      } catch (error) {
        void error;
      }
    }

    this.webBluetoothDevice = null;
    this.webBluetoothServer = null;
    this.webBluetoothCharacteristic = null;

    if (this.connectionType === WEB_BLUETOOTH_TYPE) {
      this.isConnected = false;
      this.connectionType = null;
      this.printerType = null;
      this.printerName = null;
      this.deviceInfo = null;
    }

    if (notifyDisconnect && this.callbacks.onDisconnect) {
      this.callbacks.onDisconnect();
    }
  }

  handleWebBluetoothDisconnected(event) {
    const deviceName = event?.target?.name || this.printerName || 'impressora BLE';
    this.clearWebBluetoothState({ notifyDisconnect: true });
    try {
      localStorage.removeItem('printerType');
      localStorage.removeItem('printerName');
    } catch (error) {
      void error;
    }
    console.log(`Dispositivo ${deviceName} desconectado.`);
  }

  async connectWebBluetooth() {
    try {
      if (!this.isWebBluetoothSupported()) {
        throw new Error('Seu navegador não suporta Web Bluetooth. Use Chrome ou Edge no Windows.');
      }
      if (!this.isWebBluetoothSecureContext()) {
        throw new Error('Web Bluetooth requer HTTPS ou localhost.');
      }

      const device = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'Zebra' }, { namePrefix: 'ZQ' }],
        optionalServices: [ZEBRA_BLE_SERVICE_UUID],
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(ZEBRA_BLE_SERVICE_UUID);
      const characteristic = await service.getCharacteristic(ZEBRA_BLE_WRITE_CHARACTERISTIC_UUID);

      try {
        device.removeEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);
      } catch (error) {
        void error;
      }
      device.addEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);

      this.webBluetoothDevice = device;
      this.webBluetoothServer = server;
      this.webBluetoothCharacteristic = characteristic;
      this.connectionType = WEB_BLUETOOTH_TYPE;
      this.printerType = WEB_BLUETOOTH_TYPE;
      this.isConnected = true;
      this.printerName = device.name || 'Zebra BLE';
      this.deviceInfo = {
        id: device.id || null,
        nome: device.name || 'Zebra BLE',
        transport: 'ble',
        serviceUuid: ZEBRA_BLE_SERVICE_UUID,
        characteristicUuid: ZEBRA_BLE_WRITE_CHARACTERISTIC_UUID,
      };

      localStorage.setItem('printerType', WEB_BLUETOOTH_TYPE);
      localStorage.setItem('printerName', this.printerName);

      if (this.callbacks.onConnect) {
        this.callbacks.onConnect({
          type: WEB_BLUETOOTH_TYPE,
          device: this.printerName,
          status: 'connected',
        });
      }

      return {
        name: this.printerName,
        id: device.id || null,
        characteristic,
      };
    } catch (error) {
      console.error('Erro ao conectar via Web Bluetooth:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message || 'Erro ao conectar via Web Bluetooth');
      }
      throw error;
    }
  }

  async disconnectWebBluetooth() {
    try {
      const device = this.webBluetoothDevice;
      if (device && this.handleWebBluetoothDisconnected) {
        try {
          device.removeEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);
        } catch (error) {
          void error;
        }
      }
      if (device?.gatt?.connected) {
        device.gatt.disconnect();
      }
      this.clearWebBluetoothState();
      return true;
    } catch (error) {
      console.error('Erro ao desconectar Web Bluetooth:', error);
      this.clearWebBluetoothState();
      throw error;
    }
  }

  async writeWebBluetoothChunks(textoRaw, chunkSize = WEB_BLUETOOTH_CHUNK_SIZE) {
    if (!this.webBluetoothCharacteristic) {
      throw new Error('Impressora Web Bluetooth não conectada');
    }

    const encoder = new TextEncoder();
    const bytes = encoder.encode(String(textoRaw || ''));
    const writer = typeof this.webBluetoothCharacteristic.writeValueWithoutResponse === 'function'
      ? this.webBluetoothCharacteristic.writeValueWithoutResponse.bind(this.webBluetoothCharacteristic)
      : this.webBluetoothCharacteristic.writeValue.bind(this.webBluetoothCharacteristic);

    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      const chunk = bytes.slice(offset, offset + chunkSize);
      await writer(chunk);
    }

    return true;
  }

  /**
   * Processa mensagens recebidas via WebSocket
   */
  handleWebSocketMessage(message) {
    try {

      let data = null;
      if (typeof message === 'string' && message.includes('{') && message.includes('}')) {
        try { data = JSON.parse(message); } catch (e) { void e; }
      }
      if (data && typeof data === 'object') {
        const keys = Object.keys(data);
        if (keys.includes('pareado') || keys.includes('parear')) {
          this.isConnected = true;
          this.deviceInfo = { ...(this.deviceInfo || {}), nome: '' };
          return;
        }
        if (keys.includes('sel_impressora') || keys.includes('impressora') || keys.includes('nome')) {
          const nome = extractPrinterName(data);
          if (nome) {
            this.isConnected = true;
            this.connectionType = 'socket';
            this.printerName = nome;
            this.deviceInfo = { ...(this.deviceInfo || {}), nome };
            if (this.callbacks.onConnect) {
              this.callbacks.onConnect({ type: this.connectionType || 'websocket', device: nome, status: 'connected' });
            }
          }
          return;
        }
        if (keys.includes('retorno_impressao_ok')) {
          if (this.callbacks.onMessage) this.callbacks.onMessage('retorno_impressao_ok');
          return;
        }
        if (keys.includes('retorno_impressao_error')) {
          if (this.callbacks.onError) this.callbacks.onError('retorno_impressao_error');
          return;
        }
      }
      if (message === 'retorno_impressao_ok') {
        if (this.callbacks.onMessage) this.callbacks.onMessage('retorno_impressao_ok');
      } else if (message === 'retorno_impressao_error') {
        if (this.callbacks.onError) this.callbacks.onError('Erro na impressão');
      }
    } catch (error) {
      console.error('Erro ao processar mensagem WebSocket:', error);
    }
  }

  /**
   * Envia dados para impressão via WebSocket
   */
  async sendPrintData(data) {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket não conectado'));
        return;
      }

      const originalOnMessage = this.socket.onmessage;

      this.socket.onmessage = (messageEvent) => {
        const message = messageEvent.data;
        if (message === "retorno_impressao_ok") {
          this.socket.onmessage = originalOnMessage;
          resolve();
        } else if (message === "retorno_impressao_error") {
          console.warn('Erro na impressão');
          this.socket.onmessage = originalOnMessage;
          reject(new Error('Erro na impressão'));
        }
      };

      this.socket.send(JSON.stringify(data));
    });
  }

  /**
   * Desconecta da impressora atual
   */
  async disconnect() {
    try {
      let shouldNotifyDisconnect = true;
      const wasWebBluetooth = this.connectionType === WEB_BLUETOOTH_TYPE;
      if (this.connectionType === 'bluetooth') {
        if (window.Android) {
          await window.Android.desconectar();
        }
      } else if (this.connectionType === WEB_BLUETOOTH_TYPE) {
        shouldNotifyDisconnect = false;
        await this.disconnectWebBluetooth();
      } else if (this.connectionType === 'websocket' || this.connectionType === 'socket') {
        try {
          wsPrintClient.emit({ desparear: wsPrintClient.pairedWith || '' });
        } catch (error) {
          void error;
        }
        try {
          wsPrintClient.close();
        } catch (error) {
          void error;
        }
      }

      this.isConnected = false;
      this.connectionType = null;
      this.printerType = null;
      this.printerName = null;
      this.deviceInfo = null;
      this.socket = null;
      if (!wasWebBluetooth) {
        this.webBluetoothDevice = null;
        this.webBluetoothServer = null;
        this.webBluetoothCharacteristic = null;
      }

      try {
        localStorage.removeItem('printerType');
        localStorage.removeItem('printerName');
      } catch (e) { void e; }

      if (shouldNotifyDisconnect && this.callbacks.onDisconnect) {
        this.callbacks.onDisconnect();
      }

      return true;
    } catch (error) {
      console.error('Erro ao desconectar:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message);
      }
      throw error;
    }
  }

  /**
   * Verifica se está conectado
   */
  isConnectedToPrinter() {
    if (!this.connectionType) {
      try {
        const savedType = localStorage.getItem('printerType');
        if (savedType) {
          this.connectionType = savedType;
          this.printerType = savedType;
        }
      } catch { }
    }
    if (!this.printerName) {
      try {
        const savedName = localStorage.getItem('printerName');
        if (savedName) {
          this.printerName = savedName;
        }
      } catch { }
    }

    const type = this.connectionType;

    // 1. Android Bluetooth nativo
    if (window.Android && window.Android.getPrinterName) {
      try {
        const printerName = window.Android.getPrinterName();
        if (printerName && String(printerName).trim() !== '') {
          const nome = extractPrinterName(printerName) || printerName;
          this.printerName = nome;
          this.deviceInfo = { ...(this.deviceInfo || {}), address: nome, nome };
          this.isConnected = true;
          this.connectionType = 'bluetooth';
          return true;
        }
      } catch { }
      if (type === 'bluetooth') {
        this.isConnected = false;
        return false;
      }
    }

    // 2. Web Bluetooth
    if (type === WEB_BLUETOOTH_TYPE) {
      const isGattConnected = !!(this.webBluetoothDevice?.gatt?.connected && this.webBluetoothCharacteristic);
      this.isConnected = isGattConnected;
      if (isGattConnected) {
        const nome = this.webBluetoothDevice?.name || this.printerName || 'Zebra BLE';
        this.printerName = nome;
        this.deviceInfo = {
          ...(this.deviceInfo || {}),
          id: this.webBluetoothDevice?.id || null,
          nome,
          transport: 'ble',
        };
      }
      return isGattConnected;
    }

    // 3. WebSocket / Socket
    if (type === 'websocket' || type === 'socket') {
      const isWsOpen = !!(wsPrintClient && wsPrintClient.isConnected);
      const wsName = wsPrintClient ? wsPrintClient.printerName : null;
      const nome = String(wsName || this.printerName || '').trim();
      const pairedWith = wsPrintClient?.pairedWith || this.deviceInfo?.uuid || null;

      if (isWsOpen && (pairedWith || wsName)) {
        this.isConnected = true;
        this.connectionType = 'socket';
        if (nome) this.printerName = nome;
        this.deviceInfo = {
          ...(this.deviceInfo || {}),
          uuid: pairedWith || this.deviceInfo?.uuid,
          nome: nome || this.deviceInfo?.nome || null,
        };
        return true;
      }
      this.isConnected = false;
      return false;
    }

    this.isConnected = false;
    return false;
  }

  /**
   * Obtém informações da conexão atual
   */
  getConnectionInfo() {
    const isWsOpen = !!(wsPrintClient && wsPrintClient.isConnected);
    const wsName = wsPrintClient ? wsPrintClient.printerName : null;
    const wsUuid = wsPrintClient ? wsPrintClient.uuid : null;
    const wsPaired = wsPrintClient ? wsPrintClient.pairedWith : null;
    const hasWsPrinter = isWsOpen && (!!wsPaired || String(wsName || '').trim() !== '');
    const hasBleConnection = !!(this.webBluetoothDevice?.gatt?.connected && this.webBluetoothCharacteristic);
    const hasAndroidBt = !!(typeof window !== 'undefined' && window.Android && window.Android.getPrinterName && String(window.Android.getPrinterName() || '').trim() !== '');

    const effectiveConnected = hasBleConnection || hasWsPrinter || hasAndroidBt;
    const effectiveType = effectiveConnected
      ? (hasBleConnection ? WEB_BLUETOOTH_TYPE : (hasAndroidBt ? 'bluetooth' : (hasWsPrinter ? 'socket' : this.connectionType)))
      : null;
    let effectiveDevice = this.deviceInfo ? { ...this.deviceInfo } : {};
    if (wsPaired) effectiveDevice.uuid = wsPaired;
    else if (wsUuid && !effectiveDevice.uuid) effectiveDevice.uuid = wsUuid;
    if (hasBleConnection) {
      effectiveDevice.id = this.webBluetoothDevice?.id || effectiveDevice.id || null;
      effectiveDevice.nome = this.webBluetoothDevice?.name || effectiveDevice.nome || this.printerName;
      effectiveDevice.transport = 'ble';
    }
    if (hasWsPrinter) effectiveDevice.nome = wsName || this.printerName;
    else if (hasAndroidBt) effectiveDevice.nome = window.Android.getPrinterName();
    if (!effectiveDevice || Object.keys(effectiveDevice).length === 0) effectiveDevice = null;

    return { isConnected: effectiveConnected, type: effectiveType, device: effectiveDevice };
  }

  /**
   * Testa a conexão atual
   */
  async testConnection() {
    try {
      if (this.connectionType === 'websocket') {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          // Envia um ping para testar
          this.socket.send(JSON.stringify({ type: 'ping' }));
          return true;
        }
        return false;
      } else if (this.connectionType === WEB_BLUETOOTH_TYPE) {
        return !!(this.webBluetoothDevice?.gatt?.connected && this.webBluetoothCharacteristic);
      } else if (this.connectionType === 'bluetooth') {
        // Para Bluetooth, assumimos que está conectado se o estado indica isso
        return this.isConnected;
      }
      return false;
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      return false;
    }
  }
}

// Instância singleton do serviço
const printerService = new PrinterService();

// Função global para ser chamada pelo Android WebView
window.setarImpressora = (deviceName) => {
  const normalizedDeviceName = extractPrinterName(deviceName);
  if (normalizedDeviceName) {
    printerService.connectionType = 'bluetooth';
    printerService.printerType = 'bluetooth';
    printerService.printerName = normalizedDeviceName;
    printerService.isConnected = true;
    printerService.deviceInfo = { ...(printerService.deviceInfo || {}), nome: normalizedDeviceName };
    console.log(`Impressora Bluetooth configurada via Android: ${normalizedDeviceName}`);

    // Opcional: salvar no localStorage para persistência
    localStorage.setItem('printerType', 'bluetooth');
    localStorage.setItem('printerName', normalizedDeviceName);

    // Notificar callbacks se houver
    if (printerService.callbacks.onConnect) {
      printerService.callbacks.onConnect({
        type: 'bluetooth',
        device: normalizedDeviceName,
        status: 'connected'
      });
    }
  }
};

const __printProgressListeners = new Set();
export const onPrintProgress = (cb) => {
  if (typeof cb === 'function') {
    __printProgressListeners.add(cb);
  }
  return () => {
    __printProgressListeners.delete(cb);
  };
};
const __emitPrintProgress = (p) => {
  try {
    __printProgressListeners.forEach((fn) => {
      try { fn(p); } catch (error) { void error; }
    });
  } catch (error) { void error; }
};

// Função utilitária para verificar conexão da impressora
export const verificaImpressora = () => {
  try {
    const psConnected = printerService.isConnectedToPrinter();
    return psConnected;
  } catch (error) {
    void error;
    return false;
  }
};

export const montarDtoEtiqueta = (notaObj, opts = {}) => {

  const currentUser = printerService.currentUser || null;
  const nbase = (notaObj && typeof notaObj === 'object') ? notaObj : {};
  const orgname = String(opts.orgname ?? currentUser?.organization?.name ?? '').trim();
  const branchsigla = String(opts.branchsigla ?? currentUser?.assignedBranch?.sigla ?? currentUser?.assignedBranch?.acronym ?? '').trim();
  const usuarioRaw = String(opts.usuario ?? currentUser?.name ?? notaObj?.veiculo?.placa ?? '').trim();
  const usuario = usuarioRaw.slice(0, 10);
  const now = opts.date instanceof Date ? opts.date : new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const aa = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const dtimpressao = String(opts.dtimpressao ?? `${dd}-${mm}-${aa}`);
  const hrimpressao = String(opts.hrimpressao ?? `${hh}:${min}`);

  const nfNumero = String(opts.nota ?? nbase?.nota ?? nbase?.numero ?? nbase?.nf ?? '').trim();
  const serie = String(opts.serie ?? nbase?.serie ?? nbase?.sr ?? '').trim();
  const remetente = String(
    opts.remetente ??
    nbase?.remetente ??
    notaObj?.remetenteNome ??
    notaObj?.remetente?.nome ??
    nbase?.emitente?.nome ??
    ''
  ).trim().substring(0, 35);
  const destinatario = String((opts.destinatario ?? nbase?.destinatario?.nome ?? nbase?.destinatario ?? nbase?.nomeDestinatario ?? '')).trim().substring(0, 35);
  const enderecoBase = String((opts.endereco ?? nbase?.destinatario?.endereco ?? nbase?.destinatario?.logradouro ?? nbase?.endereco ?? '')).trim().substring(0, 35);
  const nroBase = (opts.nro ?? nbase?.destinatario?.nro ?? nbase?.nro ?? '');
  const cidade = String((opts.cidade ?? nbase?.cidade ?? nbase?.destinatario?.cidade ?? '')).trim();
  const uf = String((opts.ufdestino ?? nbase?.destinatario?.uf ?? nbase?.uf ?? '')).trim();
  const rota = String(opts.rota ?? notaObj?.rota ?? nbase?.detalhe?.rota ?? nbase?.rota ?? '').trim();
  const cd = String(opts.cd ?? notaObj?.cd ?? nbase?.detalhe?.cd ?? nbase?.cd ?? nbase?.cddestinatario ?? '').trim();
  const setorVal = opts.setor ?? (typeof notaObj?.setor === 'number' ? notaObj?.setor : (nbase?.detalhe?.setor ?? nbase?.setor));
  const setor = String(setorVal ?? '').trim();
  const pedido = String(opts.pedido ?? nbase?.pedido ?? '').trim();
  const cepRawSource = (opts.cep ?? nbase?.cepentrega ?? nbase?.cep ?? '');
  const cepRaw = String(cepRawSource).replace(/\D/g, '');
  const cep = cepRaw.length === 8 ? `${cepRaw.slice(0, 5)}-${cepRaw.slice(5)}` : cepRaw;
  const enderecodestino = enderecoBase + ', ' + String(nroBase);
  const destinatarioCnpj = String((nbase?.destinatario?.cnpj ?? nbase?.cddestinatario ?? '')).replace(/[^a-zA-Z0-9]/g, '');
  const pessoafisica = destinatarioCnpj.length === 11 ? 'PF FISICA' : '';
  const volumeatual = String(opts.volumeAtual ?? '');
  const volumetotal = String(opts.volumeTotal ?? '');
  const sequencial = opts.sequencial != null ? String(opts.sequencial).padStart(8, '0') : '';
  const activeLabelCode = opts.activeLabelCode ?? printerService.activeLabelCode;
  const reimpressao = !!opts.reimpressao;

  return {
    orgname,
    branchsigla,
    usuario,
    dtimpressao,
    hrimpressao,
    rota,
    cd,
    setor,
    remetente,
    nota: nfNumero,
    serie,
    volumeatual,
    volumetotal,
    pedido,
    destinatario,
    enderecodestino,
    cep,
    cidadedestino: cidade,
    ufdestino: uf,
    sequencial,
    pessoafisica,
    activeLabelCode,
    reimpressao,
    conteudoZpl: opts.conteudoZpl || opts.zpl || null,
    zpl: opts.conteudoZpl || opts.zpl || null,
  };
};

export const enviarEtiquetaInicial = async (dados, nr1, nr2, activeLabelCode) => {
  const info = printerService.getConnectionInfo();
  const type = printerService.connectionType || info.type || (typeof window !== 'undefined' && window.Android ? 'bluetooth' : ((wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '')) ? 'websocket' : null));
  const btConnectedByName = (type === 'bluetooth' && String(printerService.printerName || '').trim() !== '');
  const clientUUID = wsPrintClient?.uuid || info?.device?.ip || null;
  const id_parear = info?.device?.uuid || wsPrintClient?.pairedWith || null;

  void activeLabelCode;

  const base = dados || {};

  console.log(dados)
  const clienteRaw = base?.nota?.remetente?.nome ?? base?.remetenteNome ?? base?.remetente?.nome ?? '';
  const cliente = String(clienteRaw).trim();
  const clienteFmt = cliente.slice(0, 30);
  const nfNumeroVal = base?.nota ?? base?.nota?.nota ?? base?.notaFiscal?.nota ?? base?.nota?.numero ?? base?.notaFiscal?.numero ?? base?.numero ?? null;
  const nfNumero = nfNumeroVal != null ? (String(nfNumeroVal).match(/\d{1,}/)?.[0] ?? '') : '';

  const v1 = String(nr1 ?? '').trim();
  const v2 = String(nr2 ?? '').trim();

  const chave = (() => {
    const c1 = base?.chaveNfe;
    const c2 = base?.chave;
    const c3 = base?.nota?.chaveNfe;
    const c4 = base?.nota?.chave;
    let c = c1 ?? c2 ?? c3 ?? c4 ?? '';
    if (!c) {
      try {
        const m = String(base?.dados || '').match(/\d{44}/);
        c = m ? m[0] : '';
      } catch (error) {
        void error;
      }
    }
    return String(c).trim();
  })();

  let zpl = '';
  zpl += '^XA';
  zpl += '^PW800';
  zpl += '^LL400';
  zpl += '^LH0,0';
  zpl += '^CI28';
  zpl += `^FO0,30^FB450,1,0,C,0^A0N,30,25^FD${clienteFmt}^FS`;
  if (nfNumero) {
    zpl += `^FO0,65^FB450,1,0,C,0^A0N,30,30^FDNota: ${nfNumero}^FS`;
  }

  if (chave) {
    zpl += `^FO480,40^BQN,2,6^FDQA,${chave}^FS`;
  }

  zpl += '^BY3,2,120';
  zpl += `^FO60,120^BCN,80,Y,N,N^FD${v1}^FS`;
  zpl += `^FO60,250^BCN,80,Y,N,N^FD${v2}^FS`;
  zpl += '^XZ';

  const useWsClient = !!(wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '' || String(wsPrintClient.uuid || '').trim() !== ''));
  const bleConnected = type === WEB_BLUETOOTH_TYPE && !!(printerService.webBluetoothDevice?.gatt?.connected && printerService.webBluetoothCharacteristic);

  if (type === WEB_BLUETOOTH_TYPE) {
    if (!bleConnected) throw new Error('Web Bluetooth desconectado');
    await printerService.writeWebBluetoothChunks(zpl);
    return true;
  }

  if (type === 'bluetooth') {
    if (window.Android) {
      window.Android.imprimir(zpl);
      return true;
    }
    if (!(info.isConnected || btConnectedByName)) throw new Error('Bluetooth indisponível e WebSocket não conectado');
  }

  if (useWsClient && (type === 'socket' || type === 'websocket')) {
    wsPrintClient.emit({ imprimir: id_parear, zpl, uuid: clientUUID });
    return true;
  }

  throw new Error('Tipo de impressora não definido');
};

// Função exportável para imprimir usando a conexão atual
export const imprimir = async (data, activeLabelCode) => {

  try { await printerService.ensureActiveLabelCode(); } catch (error) { void error; }

  printerService.isConnectedToPrinter();

  // Tenta recuperar do localStorage se estiver nulo
  if (!printerService.connectionType) {
    try {
      const savedType = localStorage.getItem('printerType');
      if (savedType) {
        printerService.connectionType = savedType;
        printerService.printerType = savedType;
      }
    } catch { }
  }
  if (!printerService.printerName) {
    try {
      const savedName = localStorage.getItem('printerName');
      if (savedName) printerService.printerName = savedName;
    } catch { }
  }

  let type = printerService.connectionType;

  if (!type) {
    if (typeof window !== 'undefined' && window.Android) {
      type = 'bluetooth';
      printerService.connectionType = 'bluetooth';
    } else if (wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '')) {
      type = 'websocket';
      printerService.connectionType = 'websocket';
    }
  }

  if (!type && typeof window !== 'undefined' && window.Android) {
    type = 'bluetooth';
    printerService.connectionType = 'bluetooth';
  }

  const finalPrinter = (
    extractPrinterName(printerService.printerName) ||
    extractPrinterName(wsPrintClient ? wsPrintClient.printerName : null) ||
    extractPrinterName(data?.printername) ||
    extractPrinterName(data?.impressora) ||
    extractPrinterName(data?.printer) ||
    extractPrinterName(data?.deviceName)
  );

  if (finalPrinter && !printerService.printerName) {
    printerService.printerName = finalPrinter;
    printerService.isConnected = true;
  }

  const btByName = String(printerService.printerName || finalPrinter || '').trim() !== '';
  const isConnected = !!printerService.isConnected || !!(wsPrintClient && wsPrintClient.isConnected) || btByName;

  if (!isConnected || !finalPrinter) {
    try { message.error('Impressora não conectada'); } catch (error) { void error; }
    throw new Error('Impressora não conectada');
  }


  const currentUser = printerService.currentUser || null;
  const chave = (() => {
    const c1 = data?.chaveNfe;
    const c2 = data?.chave;
    const c3 = data?.nota?.chaveNfe;
    const c4 = data?.nota?.chave;
    let c = c1 ?? c2 ?? c3 ?? c4 ?? '';
    if (!c) {
      try {
        const m = String(data?.dados || '').match(/\d{44}/);
        c = m ? m[0] : '';
      } catch (error) {
        void error;
      }
    }
    return String(c).trim();
  })();
  if (!chave) throw new Error('Chave da NF-e não informada');

  data.chave = chave;
  if (data.nota && typeof data.nota === 'object') {
    data.nota.chave = chave;
  }

  const filialRaw = currentUser?.assignedBranch?.codigo;
  const filialInt = filialRaw == null ? NaN : parseInt(String(filialRaw), 10);

  const payload = {
    chave,
    userId: (data?.userId || data?.usuarioId || currentUser?.id) || null,
    organizationId: (data?.organizationId || currentUser?.organizationId) || null,
    assignedBranchId: (data?.assignedBranchId || currentUser?.assignedBranchId) || null,
    printername: (finalPrinter || '').slice(0, 100),
    filial: Number.isInteger(filialInt) ? filialInt : undefined,
    placa: data?.nota?.veiculo?.placa || data?.placa || null,
    veiculoId: data?.veiculoId || data?.nota?.veiculo?.id || null,
    // obrigatórios no ImprimirDto
    cnpjrmte: (() => {
      let c = (data?.nota?.cnpjrmte ??
        data?.cdremetente ??
        data?.cnpjrmte ??
        data?.nota?.CNPJ ?? '');

      // Fallback: extrair da chave (posições 7-20, ou seja, index 6-20)
      if ((!c || String(c).trim() === '') && chave.length === 44) {
        c = chave.substring(6, 20);
      }

      return String(c || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
    })(),
    nota: (() => {
      let n = (data?.nota?.nota ?? data?.notaNumero ?? data?.nota_numero ?? data?.notaNro);

      // Se n for nulo e data.nota for número, usamos data.nota
      if (n == null && typeof data?.nota === 'number') {
        n = data.nota;
      }

      // Fallback: extrair da chave (posições 26-34, ou seja, index 25-34)
      if (n == null && chave.length === 44) {
        n = parseInt(chave.substring(25, 34), 10);
      }

      return n == null ? undefined : parseInt(String(n), 10);
    })(),
    serie: (() => {
      let s = (data?.nota?.serie ?? data?.serie ?? '');

      // Fallback: extrair da chave (posições 23-25, ou seja, index 22-25)
      if ((!s || String(s).trim() === '') && chave.length === 44) {
        s = chave.substring(22, 25);
      }

      return String(s || '').slice(0, 3);
    })(),
    volumes: (() => {
      const v = (data?.nota?.detalhe?.volumes ?? data?.nota?.volumes ?? data?.volumes);
      return v == null ? undefined : parseInt(String(v), 10);
    })(),
    // opcionais
    rota: data?.rota || data?.nota?.detalhe?.rota || data?.nota?.rota || undefined,
    cd: data?.cd || data?.nota?.detalhe?.cd || data?.nota?.cd || undefined,
    setor: (typeof data?.setor === 'number' ? data?.setor : (data?.nota?.detalhe?.setor ?? data?.nota?.setor)),
    cepentrega: data?.cepentrega || data?.nota?.cepentrega || undefined,
    isSsw: Boolean(data?.isSsw || data?.nota?.isSsw || data?.origem === 'ssw'),
    origem: data?.origem || data?.nota?.origem || undefined,
    etiquetas: data?.etiquetas || data?.nota?.etiquetas || undefined,
  };

  //aviso de quantidade alta de volumes
  if (payload.volumes > 100) {
    try { message.warning('Quantidade de volumes maior que 100. A impressão pode demorar.\nEm dispositivos móveis não bloqueia a interface.'); } catch (error) { void error; }
  }

  //return;
  const res = await notaanota.imprimir(payload);
  // Validação do retorno da requisição
  if (!res || res.ok === false || res.status !== 200) {
    if (res?.message) {
      try { message.error(res.message); } catch (error) { void error; }
    }
    return false;
  }

  const isSsw = Boolean(res?.isSsw || data?.isSsw || data?.nota?.isSsw || data?.origem === 'ssw');

  let baseNr1 = 0;
  let baseNr2 = 0;

  if (!isSsw) {
    // Extração e validação obrigatória de nr1 e nr2 (não podem ser 0, vazios ou estáticos)
    baseNr1 = parseInt(res?.nr1Num ?? res?.nr1 ?? 0);
    baseNr2 = parseInt(res?.nr2Num ?? res?.nr2 ?? 0);

    if (!baseNr1 || !baseNr2 || isNaN(baseNr1) || isNaN(baseNr2)) {
      console.error('nr1 ou nr2 inválidos no retorno:', { nr1: baseNr1, nr2: baseNr2 });
      return false;
    }
  }

  try {
    let volTotal = 0;
    let lastVol = 0;

    const volFromNota = Number((data?.nota?.detalhe?.volumes ?? data?.nota?.volumes ?? 0));
    // Prioriza o totalVolumes retornado pelo backend
    const totalVolumesCalculado = res?.totalVolumes ?? (!isSsw && baseNr2 > baseNr1 ? (baseNr2 - baseNr1) : null);
    volTotal = Number(totalVolumesCalculado || volFromNota || 0);
    __emitPrintProgress({ total: volTotal, current: 0 });
    let etiquetasList = res?.etiquetas || data?.etiquetas || data?.nota?.etiquetas || [];
    const chaveNota = res?.chave || data?.chave || data?.nota?.chave || payload?.chave;

    // Se faltarem dados de etiquetas (ex: grande quantidade de volumes) ou se não houver conteudoZpl válido, busca paginado do backend
    if (chaveNota && isSsw && (etiquetasList.length < volTotal || !etiquetasList.some((e) => e.conteudoZpl && e.conteudoZpl.startsWith('^XA')))) {
      try {
        const respEtq = await notaanota.getEtiquetas(chaveNota, 1, Math.min(volTotal, 100));
        if (respEtq?.etiquetas && respEtq.etiquetas.length > 0) {
          etiquetasList = respEtq.etiquetas;
        }
      } catch (err) {
        console.warn('[printerService] Falha ao pré-carregar etiquetas com ZPL:', err);
      }
    }

    let printedOk = true;
    for (let voli = 1; voli <= volTotal; voli++) {
      let seqStr = '';
      let zplVolume = null;

      if (isSsw) {
        let etqVol = etiquetasList.find(
          (e) => Number(e.volume) === Number(voli) || String(e.volume).trim() === String(voli)
        );

        // Se a nota tiver mais volumes e este volume estiver em página posterior
        if (!etqVol && chaveNota) {
          const pageNeeded = Math.floor((voli - 1) / 100) + 1;
          try {
            const respNext = await notaanota.getEtiquetas(chaveNota, pageNeeded, 100);
            if (respNext?.etiquetas) {
              etiquetasList = [...etiquetasList, ...respNext.etiquetas];
              etqVol = etiquetasList.find(
                (e) => Number(e.volume) === Number(voli) || String(e.volume).trim() === String(voli)
              );
            }
          } catch (e) {
            void e;
          }
        }

        if (!etqVol && etiquetasList[voli - 1]) {
          etqVol = etiquetasList[voli - 1];
        }

        zplVolume = etqVol?.conteudoZpl || etqVol?.conteudo_zpl || null;
        if (zplVolume && !zplVolume.startsWith('^XA')) {
          zplVolume = null;
        }
        const seqVal = etqVol?.sequencial ?? etqVol?.numeroEtiqueta ?? etqVol?.labelNumber ?? voli;
        seqStr = String(seqVal);
      } else {
        const seq = baseNr1 + voli;
        seqStr = String(seq).padStart(8, '0');
      }

      const dto = montarDtoEtiqueta(data?.nota || null, {
        volumeAtual: String(voli),
        volumeTotal: String(volTotal),
        sequencial: seqStr,
        reimpressao: Boolean(res?.isReimpressao || data?.reimpressao),
        activeLabelCode: activeLabelCode ?? printerService.activeLabelCode,
        rota: data?.rota,
        cd: data?.cd,
        setor: (typeof data?.setor === 'number' ? data?.setor : undefined),
        pedido: data?.nota?.pedido,
        cep: data?.cepentrega,
        usuario: (printerService.currentUser?.name || data?.placa || ''),
        conteudoZpl: zplVolume,
        zpl: zplVolume,
      });

      // Envia uma a uma para a impressora utilizando o ZPL da coluna etiquetas.conteudoZpl
      await enviarparaimpressora(dto).catch((err) => {
        console.error(`[printerService] Falha ao imprimir volume ${voli}:`, err);
        printedOk = false;
      });
      if (!printedOk) break;
      lastVol = voli;
      __emitPrintProgress({ total: volTotal, current: voli });

      // Pausa entre o envio de cada etiqueta para processamento suave no buffer térmico
      const sleepMs = volTotal > 50 ? 100 : 60;
      await new Promise((resolve) => setTimeout(resolve, sleepMs));

      if (voli % 10 === 0 && voli < volTotal) {
        const delayMs = volTotal > 499 ? 3000 : 1200;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    __emitPrintProgress({ total: volTotal, current: lastVol, done: true, ok: printedOk });

    if (!isSsw && !res?.isReimpressao) {
      try {
        await enviarEtiquetaInicial(data?.nota || null, res.nr1, res.nr2, printerService.activeLabelCode);
      } catch (error) {
        void error;
        try { message.warning('Falha ao imprimir etiqueta inicial'); } catch (messageError) { void messageError; }
      }
    }

    if (printedOk && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('etiquetasImpressas', {
          detail: {
            chave: data?.chave || data?.chaveNfe || data?.nota?.chave || null,
            volumes: volTotal,
            reimpressao: Boolean(res?.isReimpressao || data?.reimpressao),
            timestamp: Date.now()
          }
        }));
      } catch { }
    }

    return res;

  } catch (error) {
    void error;
    try { __emitPrintProgress({ total: 0, current: 0, done: true, ok: false }); } catch (progressError) { void progressError; }
    return false;
  }

};

// Função para enviar diretamente para impressora com base na nota atual e intervalo de volumes
export const enviarparaimpressora = async (dto) => {

  printerService.isConnectedToPrinter();

  if (!printerService.connectionType) {
    const savedType = localStorage.getItem('printerType');
    if (savedType) {
      printerService.connectionType = savedType;
      printerService.printerType = savedType;
    }
  }
  if (!printerService.printerName) {
    const savedName = localStorage.getItem('printerName');
    if (savedName) {
      printerService.printerName = savedName;
    }
  }

  let type = printerService.connectionType;

  if (!type) {
    if (printerService.webBluetoothDevice?.gatt?.connected && printerService.webBluetoothCharacteristic) {
      type = WEB_BLUETOOTH_TYPE;
      printerService.connectionType = WEB_BLUETOOTH_TYPE;
    } else if (typeof window !== 'undefined' && window.Android && window.Android.getPrinterName) {
      type = 'bluetooth';
      printerService.connectionType = 'bluetooth';
    } else if (wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '')) {
      type = 'websocket';
      printerService.connectionType = 'websocket';
    }
  }

  if (!type && typeof window !== 'undefined' && window.Android) {
    type = 'bluetooth';
    printerService.connectionType = 'bluetooth';
  }

  const info = printerService.getConnectionInfo();
  const btConnectedByName = (type === 'bluetooth' && String(printerService.printerName || '').trim() !== '');
  const clientUUID = wsPrintClient?.uuid || info?.device?.ip || null;
  let id_parear = info?.device?.uuid || wsPrintClient?.pairedWith || null;
  if (!id_parear && typeof localStorage !== 'undefined') {
    try {
      id_parear = localStorage.getItem('wsPrintPairedWith') || localStorage.getItem('printerDevice') || null;
    } catch { }
  }

  let zpl = '';
  const rawZpl = typeof dto === 'string'
    ? dto
    : (dto?.conteudoZpl || dto?.conteudo_zpl || dto?.zpl || null);

  if (rawZpl && typeof rawZpl === 'string' && rawZpl.trim().startsWith('^XA')) {
    // Usa diretamente o código ZPL oficial extraído e persistido na coluna etiquetas.conteudoZpl
    zpl = rawZpl.trim();
  } else {
    const currentUser = printerService.currentUser || null;
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const aa = String(now.getFullYear()).slice(-2);
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const dtDefault = `${dd}-${mm}-${aa}`;
    const hrDefault = `${hh}:${min}`;

    zpl = `
      ^XA~SD15^PRC^MMT^LH0030,0040^FS
      ^FO210,0^AAN,18,10^FD$orgname^FS
      ^FO210,20^AAN,18,10^FD$branchsigla $usuario $dtimpressao $hrimpressao ETQAPP^FS
      ^FO210,42^GB419,118,59,B^FS
      ^FO238,55^A0N,120,100^FR^FD$rota^FS
      ^FO475,55^FB144,1,0,R,0^A0N,45,45^FR^FD$cd^FS
      ^FO460,101^A0N,60,70^FR^FB159,1,0,R,0^FD$setor^FS
      ^FO210,168^AAN,18,10^FD$remetente^FS
      ^FO210,194^AAN,18,10^FDNF^FS
      ^FO235,194^A0N,45,50^FD$nota^FS
      ^FO410,213^AAN,18,10^FD$serie^FS
      ^FO430,194^A0N,45,50^FB199,1,0,R,0^FD$volumeatual/$volumetotal^FS
      ^FO210,238^AAN,18,10^FD$pedido^FS
      ^FO210,260^GB419,2,2^FS
      ^FO210,268^AAN,18,10^FD$destinatario^FS
      ^FO210,312^A0N,25,20^FB419,3,0,L,0
      ^FD$enderecodestino^FS
      ^FO210,290^AAN,18,10^FD$cep $cidadedestino/$ufdestino^FS
      ^FO45,0^BY4.0,2.5^BCR,140,N,N,N,A^FD$sequencial^FS
      ^FO10,70^A0R,30,30^FD$sequencial^FS
      ^XZ
    `;

  try {
    const orgDominio = String(dto?.orgdominio ?? dto?.orgname ?? currentUser?.organization?.dominio ?? '').trim();
    const branchSigla = String(dto?.branchsigla ?? currentUser?.assignedBranch?.sigla ?? currentUser?.assignedBranch?.acronym ?? '').trim();
    const usuario = String(dto?.usuario ?? '').trim();
    const dtimpressao = String(dto?.dtimpressao ?? dtDefault);
    const hrimpressao = String(dto?.hrimpressao ?? hrDefault);
    const rotaF = String(dto?.rota ?? '');
    const cdF = String(dto?.cd ?? '');
    const setorF = String(dto?.setor ?? '');
    const remetente = String(dto?.remetente ?? '');
    const nfNumeroF = String(dto?.nota ?? '');
    const serieF = String(dto?.serie ?? '');
    const volumeatual = String(dto?.volumeatual ?? '');
    const volumetotal = String(dto?.volumetotal ?? '');
    const pedido = String(dto?.pedido ?? '');
    const destinatarioF = String(dto?.destinatario ?? '');
    const cep = String(dto?.cep ?? '');
    const cidade = String(dto?.cidadedestino ?? '');
    const ufdestino = String(dto?.ufdestino ?? '');
    const enderecoFinal = String(dto?.enderecodestino ?? '');
    const nrCode = String(dto?.sequencial ?? '');

    if (!nrCode) throw new Error('Sequencial é obrigatório');

    const pessoafisica = dto?.pessoafisica === 'PF FISICA' ? 'PF FISICA' : '';

    zpl = zpl.replace('$orgname', orgDominio);
    zpl = zpl.replace('$branchsigla', branchSigla);
    zpl = zpl.replace('$usuario', usuario);
    zpl = zpl.replace('$dtimpressao', dtimpressao);
    zpl = zpl.replace('$hrimpressao', hrimpressao);
    zpl = zpl.replace('$rota', rotaF);
    zpl = zpl.replace('$cd', cdF);
    zpl = zpl.replace('$setor', setorF);
    zpl = zpl.replace('$remetente', remetente);
    zpl = zpl.replace('$nota', nfNumeroF);
    zpl = zpl.replace('$serie', serieF);
    zpl = zpl.replace('$volumeatual', volumeatual);
    zpl = zpl.replace('$volumetotal', volumetotal);
    zpl = zpl.replace('$pedido', pedido);
    zpl = zpl.replace('$destinatario', destinatarioF);
    zpl = zpl.replace('$cep', cep);
    zpl = zpl.replace('$cidadedestino', cidade);
    zpl = zpl.replace('$ufdestino', ufdestino);
    zpl = zpl.replace('$enderecodestino', enderecoFinal);
    zpl = zpl.replace('$sequencial', nrCode);
    zpl = zpl.replace('$sequencial', nrCode);
    if (pessoafisica === 'PF FISICA') {
      //zpl = zpl.replace('$pessoafisica', pessoafisica);
    } else {
      //zpl = zpl.replace('^FO250,0^GB36,336,30,B^FS','');
      //zpl = zpl.replace('^FO250,113^A0R,30,30^FR^FD$pessoafisica^FS', '');
    }
  } catch (error) {
    if (error.message === 'Sequencial é obrigatório') throw error;
  }
  }

  const bleConnected = type === WEB_BLUETOOTH_TYPE && !!(printerService.webBluetoothDevice?.gatt?.connected && printerService.webBluetoothCharacteristic);
  if (type === WEB_BLUETOOTH_TYPE || printerService.printerType === WEB_BLUETOOTH_TYPE) {
    if (!bleConnected) {
      throw new Error('Web Bluetooth desconectado');
    }
    await printerService.writeWebBluetoothChunks(zpl);
    return true;
  }

  if (type === 'bluetooth') {
    if (window.Android) {
      window.Android.imprimir(zpl);
      return true;
    }
    if (!(info.isConnected || btConnectedByName)) throw new Error('Bluetooth indisponível e WebSocket não conectado');
  }

  const useWsClient = !!(wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '' || String(wsPrintClient.uuid || '').trim() !== ''));
  if (useWsClient && (type === 'socket' || type === 'websocket')) {
    wsPrintClient.emit({ imprimir: id_parear, zpl, uuid: clientUUID });
    return true;
  }

  throw new Error('Tipo de impressora não definido');
};

export default printerService;
